/**
 * App version and release date for the home page label and About (#350).
 *
 * `__APP_VERSION__` / `__APP_BUILD_DATE__` come from vite.config.ts. The
 * helpers below are pure so the label and About copy are unit tested.
 */
import { just, matchMaybe, nothing, type Maybe } from "./maybe";

export const APP_NAME = "Zephyrwrite";

export function appVersion(): string {
  return __APP_VERSION__;
}

/** Release date from the build, or Nothing when the build had none. */
export function appReleaseDate(): Maybe<string> {
  return releaseDateFromDefine(__APP_BUILD_DATE__);
}

/** Vite dev server (not a bundled release). */
export function isDevBuild(): boolean {
  return import.meta.env.DEV === true;
}

export function releaseDateFromDefine(raw: string): Maybe<string> {
  const trimmed = raw.trim();
  return trimmed.length === 0 ? nothing : just(trimmed);
}

/** Home page label text, for example "v0.1.10". */
export function versionLabel(version: string): string {
  return `v${version}`;
}

export function aboutAriaLabel(version: string): string {
  return `About ${APP_NAME}, version ${version}`;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/**
 * Calendar date from an ISO 8601 string, read from its own date part so a
 * late-evening commit keeps the committer's day (no time zone shift).
 */
export function formatReleaseDate(iso: string): Maybe<string> {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (m === null) return nothing;
  const month = MONTHS[Number(m[2]) - 1];
  if (typeof month === "undefined") return nothing;
  const day = Number(m[3]);
  if (day < 1 || day > 31) return nothing;
  return just(`${month} ${day}, ${m[1]}`);
}

/** About line under the version: release date, "Dev build", or unknown. */
export function releaseDateText(
  releaseDate: Maybe<string>,
  devBuild: boolean,
): string {
  if (devBuild) return "Dev build";
  return matchMaybe(releaseDate, {
    nothing: () => "Release date unknown",
    just: (iso) =>
      matchMaybe(formatReleaseDate(iso), {
        nothing: () => "Release date unknown",
        just: (date) => `Released ${date}`,
      }),
  });
}

/** Text placed on the clipboard by "Copy version". */
export function copyVersionText(
  version: string,
  releaseDate: Maybe<string>,
  devBuild: boolean,
): string {
  if (devBuild) return `${APP_NAME} ${version} (dev build)`;
  return matchMaybe(releaseDate, {
    nothing: () => `${APP_NAME} ${version}`,
    just: (iso) =>
      matchMaybe(formatReleaseDate(iso), {
        nothing: () => `${APP_NAME} ${version}`,
        just: (date) => `${APP_NAME} ${version} (${date})`,
      }),
  });
}
